import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { useEffectiveRole } from "../../../lib/roles";
import { toTemplateSummary, type TenantTemplateSummary } from "../lib/contracts";
import { mapApplyError, type SafeApplyError } from "../lib/applyErrors";
import { useOnboarding } from "../lib/OnboardingProvider";
import { useDialogA11y } from "../lib/useDialogA11y";

/** In-memory binding for a single template-application action.
 *
 *  ``key`` is a plain ``crypto.randomUUID()`` — an opaque
 *  cryptographically random value that carries NO business or
 *  contextual information. The five surrounding fields
 *  (``tenantId``, ``templateKey``, ``templateVersion``, ``sessionIdentity``)
 *  are kept ONLY in the client's memory so we can decide, on retry,
 *  whether the current attempt is "the same in-flight action" (reuse
 *  ``key``) or a new action (mint a fresh ``key``). They are NEVER
 *  transmitted, logged, persisted, or serialized. */
interface ApplyAttempt {
  tenantId: string;
  templateKey: string;
  templateVersion: string;
  sessionIdentity: string;
  key: string; // opaque UUID sent as Idempotency-Key
}

function newAttempt(
  tenantId: string,
  templateKey: string,
  templateVersion: string,
  sessionIdentity: string,
): ApplyAttempt {
  return {
    tenantId,
    templateKey,
    templateVersion,
    sessionIdentity,
    key: crypto.randomUUID(),
  };
}

/** An attempt binds only if EVERY field matches the current call site.
 *  Any diff (template change, version bump, tenant switch, session
 *  change) forces a fresh opaque UUID. */
function attemptMatches(
  a: ApplyAttempt | null,
  tenantId: string,
  templateKey: string,
  templateVersion: string,
  sessionIdentity: string,
): boolean {
  return !!a &&
    a.tenantId === tenantId &&
    a.templateKey === templateKey &&
    a.templateVersion === templateVersion &&
    a.sessionIdentity === sessionIdentity;
}

export function TemplateSelectionPanel() {
  const role = useEffectiveRole();
  const { state, refresh, activeTenantId, sessionIdentity } = useOnboarding();
  const isOwner = role === "owner_admin" || role === "super_admin";
  const canView = isOwner || role === "manager_trainer";
  const [catalog, setCatalog] = useState<TenantTemplateSummary[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<SafeApplyError | null>(null);
  const [applyOk, setApplyOk] = useState(false);
  const attemptRef = useRef<ApplyAttempt | null>(null);
  const inflightRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Reset all local state whenever the active tenant OR session identity
  // changes. This handles: tenant switch, support-target switch, support
  // end, login/logout. Any in-progress attempt is discarded so no
  // opaque key can bleed across tenants or sessions.
  useEffect(() => {
    setCatalog(null);
    setCatalogError(null);
    setSelectedKey(null);
    setConfirmOpen(false);
    setApplying(false);
    setApplyError(null);
    setApplyOk(false);
    attemptRef.current = null;
    inflightRef.current = false;
  }, [activeTenantId, sessionIdentity]);

  useEffect(() => {
    if (!canView) return;
    if (!activeTenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/tenant/templates");
        if (cancelled) return;
        const rows = Array.isArray(r?.data?.templates) ? r.data.templates : [];
        setCatalog(rows.map(toTemplateSummary).filter(Boolean) as TenantTemplateSummary[]);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 401) return;
        if (!cancelled) setCatalogError("The template catalog is temporarily unavailable.");
      }
    })();
    return () => { cancelled = true; };
  }, [canView, activeTenantId, sessionIdentity]);

  const chooseTemplate = (key: string) => {
    setSelectedKey(key);
    // Selection change invalidates any prior attempt.
    attemptRef.current = null;
    setApplyError(null);
    setApplyOk(false);
  };

  const appliedKey = state?.selected_template_key ?? null;
  const appliedVer = state?.selected_template_version ?? null;
  const appliedSummary = useMemo(() => {
    if (!appliedKey) return null;
    return catalog?.find((t) => t.key === appliedKey) ?? null;
  }, [catalog, appliedKey]);

  const catalogVersionMismatch = !!(appliedKey && appliedVer && appliedSummary
    && appliedSummary.template_version !== appliedVer);
  const missingFromCatalog = !!(appliedKey && catalog && !appliedSummary);

  const submit = async (templateKey: string) => {
    if (inflightRef.current) return;
    if (!activeTenantId || !sessionIdentity) return;
    // Look up the version at submit time so a catalog refresh between
    // Continue and Apply is treated as a version change.
    const chosen = catalog?.find((t) => t.key === templateKey);
    if (!chosen) return;
    const templateVersion = chosen.template_version;

    inflightRef.current = true;
    setApplying(true); setApplyError(null); setApplyOk(false);

    // Reuse only when EVERY binding field matches; otherwise mint a
    // fresh opaque UUID.
    if (!attemptMatches(
      attemptRef.current,
      activeTenantId,
      templateKey,
      templateVersion,
      sessionIdentity,
    )) {
      attemptRef.current = newAttempt(
        activeTenantId, templateKey, templateVersion, sessionIdentity,
      );
    }
    const attempt = attemptRef.current!;

    try {
      await api.post(
        "/tenant/apply-template",
        { template_code: templateKey },
        // Header carries ONLY the opaque UUID. No tenant id, no template
        // key, no version, no session or user identifier.
        { headers: { "Idempotency-Key": attempt.key } },
      );
      // Success — attempt is resolved; clear binding.
      attemptRef.current = null;
      setApplyOk(true);
      setConfirmOpen(false);
      await refresh();
    } catch (e) {
      const mapped = mapApplyError(e);
      setApplyError(mapped);
      if (mapped.refreshCatalog) {
        try {
          const r = await api.get("/tenant/templates");
          const rows = Array.isArray(r?.data?.templates) ? r.data.templates : [];
          setCatalog(rows.map(toTemplateSummary).filter(Boolean) as TenantTemplateSummary[]);
        } catch { /* ignore */ }
      }
      // Definitive non-retryable failure — burn the attempt so any
      // future submit starts fresh with a new opaque UUID.
      if (!mapped.allowRetry) attemptRef.current = null;
    } finally {
      inflightRef.current = false;
      setApplying(false);
    }
  };

  const closeDialog = () => {
    setConfirmOpen(false);
    setApplyError(null);
    // Cancel discards the in-progress attempt: subsequent Continue →
    // Apply mints a new opaque UUID.
    attemptRef.current = null;
  };

  useDialogA11y(confirmOpen, dialogRef, closeDialog);

  if (!canView) return null;
  if (!activeTenantId) return null;

  return (
    <section data-testid="template-selection-panel" aria-labelledby="tsp-heading" className="rounded-lg border bg-white p-5 dark:bg-slate-900">
      <h2 id="tsp-heading" className="text-lg font-semibold">CRM Template</h2>

      {appliedKey && (
        <div className="mt-3 rounded-md border p-3 dark:border-slate-700" data-testid="applied-template-summary">
          <p className="text-sm">
            <span className="font-medium" data-testid="applied-template-name">
              {appliedSummary?.display_name || appliedKey}
            </span>{" "}
            <span className="text-slate-500" data-testid="applied-template-version">v{appliedVer}</span>
          </p>
          {catalogVersionMismatch && (
            <p className="mt-1 text-xs text-amber-700" data-testid="applied-template-newer-catalog-note">
              A newer catalog version (v{appliedSummary?.template_version}) is available. Your tenant remains on v{appliedVer}.
            </p>
          )}
          {missingFromCatalog && (
            <p className="mt-1 text-xs text-slate-600" data-testid="applied-template-legacy-note">
              This tenant uses a previously configured template version.
            </p>
          )}
        </div>
      )}

      {catalogError && (
        <p className="mt-3 text-sm text-amber-800" data-testid="template-catalog-error">{catalogError}</p>
      )}

      {!appliedKey && isOwner && catalog && (
        <>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
            Select the CRM template that will become this tenant's configured foundation.
            Changing or upgrading it later requires RMBCS support.
          </p>
          <ul className="mt-3 grid gap-3 md:grid-cols-2" role="radiogroup" aria-labelledby="tsp-heading">
            {catalog.map((t) => (
              <li key={t.key}>
                <label
                  className={`block cursor-pointer rounded-md border p-3 ${selectedKey === t.key ? "border-blue-600 ring-2 ring-blue-600" : ""} dark:border-slate-700`}
                  data-testid={`template-card-${t.key}`}
                >
                  <input
                    type="radio" className="sr-only" name="template"
                    checked={selectedKey === t.key}
                    onChange={() => chooseTemplate(t.key)}
                    data-testid={`template-radio-${t.key}`}
                    aria-label={`Select ${t.display_name}`}
                  />
                  <p className="text-sm font-semibold">{t.display_name}</p>
                  <p className="text-xs text-slate-500">v{t.template_version}</p>
                  <p className="mt-1 text-sm">{t.description}</p>
                </label>
              </li>
            ))}
          </ul>
          <button type="button" disabled={!selectedKey}
            onClick={() => setConfirmOpen(true)}
            data-testid="apply-template-open-confirm"
            className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900">
            Continue
          </button>
        </>
      )}

      {confirmOpen && selectedKey && (
        <div role="dialog" aria-modal="true" aria-labelledby="apply-confirm-heading"
          data-testid="apply-template-confirm-dialog"
          ref={dialogRef}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 dark:bg-slate-900">
            <h3 id="apply-confirm-heading" className="text-base font-semibold">
              Confirm CRM template
            </h3>
            <p className="mt-2 text-sm">
              You are about to apply <strong>{catalog?.find((t) => t.key === selectedKey)?.display_name}</strong> as this tenant's CRM foundation.
              Changing or upgrading it later requires RMBCS support.
            </p>
            {applyError && (
              <div role="alert" data-testid="apply-template-error"
                className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">{applyError.title}</p>
                <p>{applyError.message}</p>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={closeDialog}
                data-testid="apply-template-cancel"
                className="rounded-md border px-3 py-1.5 text-sm dark:border-slate-700">
                Cancel
              </button>
              <button type="button" disabled={applying} onClick={() => submit(selectedKey)}
                data-testid="apply-template-confirm"
                className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900">
                {applying ? "Applying template…" : (applyError?.allowRetry ? "Retry" : "Apply template")}
              </button>
            </div>
          </div>
        </div>
      )}

      {applyOk && (
        <p role="status" aria-live="polite" data-testid="apply-template-success"
          className="mt-3 text-sm text-emerald-700">Template applied.</p>
      )}
    </section>
  );
}
